import type { LandIqDataRunDetail } from '@immo/api-contract';

export const POLL_INTERVAL_MS = 5_000;

export type LogLine = LandIqDataRunDetail['log']['lines'][number];

export type PollRead = { delay: number; logAfter: string | null };

export type RunPoll = {
  detail: LandIqDataRunDetail;
  lines: LogLine[];
  cursor: string | null;
  next: PollRead | null;
  failure: { code: string | undefined } | null;
};

export type RunPollAction =
  | { type: 'answered'; read: PollRead; answer: LandIqDataRunDetail }
  | { type: 'failed'; code: string | undefined }
  | { type: 'reread' };

const nextRead = (detail: LandIqDataRunDetail, cursor: string | null): PollRead | null =>
  detail.run?.state === 'RUNNING' ? { delay: POLL_INTERVAL_MS, logAfter: cursor } : null;

export function initRunPoll(detail: LandIqDataRunDetail): RunPoll {
  return {
    detail,
    lines: detail.log.lines,
    cursor: detail.log.nextCursor,
    next: nextRead(detail, detail.log.nextCursor),
    failure: null,
  };
}

/**
 * A read with a cursor appends and keeps the last non-null cursor; a read without one replaces the tail,
 * so a cursor from before it (possibly a previous run's) never survives.
 */
export function runPollReducer(state: RunPoll, action: RunPollAction): RunPoll {
  if (action.type === 'failed') return { ...state, next: null, failure: { code: action.code } };

  if (action.type === 'reread') return { ...state, next: { delay: 0, logAfter: null }, failure: null };

  const { answer, read } = action;
  const appended = read.logAfter !== null;
  const cursor = appended ? (answer.log.nextCursor ?? state.cursor) : answer.log.nextCursor;

  return {
    detail: answer,
    lines: appended ? [...state.lines, ...answer.log.lines] : answer.log.lines,
    cursor,
    next: nextRead(answer, cursor),
    failure: null,
  };
}
